(() => {
  const getMainSection = () => document.querySelector('[id^="shopify-section-template"][id$="__main"]');

  if (!customElements.get('product-scroll-story')) {
    customElements.define(
      'product-scroll-story',
      class ProductScrollStory extends HTMLElement {
        constructor() {
          super();
          this.images = [];
          this.activeIndex = -1;
          this.rafId = null;
          this.handleScroll = this.handleScroll.bind(this);
          this.update = this.update.bind(this);
        }

        connectedCallback() {
          this.track = this.querySelector('[data-pss-track]');
          this.images = Array.from(this.querySelectorAll('[data-pss-image]'));
          if (!this.track || this.images.length < 2) return;

          this.activeIndex = Math.max(0, this.images.findIndex((image) => image.classList.contains('is-active')));
          if (this.activeIndex < 0) this.activeIndex = 0;

          window.addEventListener('scroll', this.handleScroll, { passive: true });
          window.addEventListener('resize', this.handleScroll);
          this.update();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.handleScroll);
          window.removeEventListener('resize', this.handleScroll);
          if (this.rafId) window.cancelAnimationFrame(this.rafId);
        }

        handleScroll() {
          if (this.rafId) return;
          this.rafId = window.requestAnimationFrame(() => {
            this.rafId = null;
            this.update();
          });
        }

        update() {
          if (!this.track || this.images.length < 2) return;

          const rect = this.track.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
          const scrollable = Math.max(this.track.offsetHeight - viewportHeight, 1);
          const progress = Math.min(Math.max(-rect.top / scrollable, 0), 1);
          const index = Math.min(this.images.length - 1, Math.round(progress * (this.images.length - 1)));
          this.setActive(index);
        }

        setActive(index) {
          if (index === this.activeIndex) return;
          this.activeIndex = index;

          this.images.forEach((image, imageIndex) => {
            const isActive = imageIndex === index;
            image.classList.toggle('is-active', isActive);
            image.style.opacity = isActive ? '1' : '0';
            image.style.zIndex = isActive ? '2' : '1';
          });
        }
      }
    );
  }

  const injectStyles = (mainSection, story) => {
    if (!mainSection || !story || mainSection.querySelector('[data-pss-rescue-style]')) return;

    const mainProduct = mainSection.querySelector('[id^="MainProduct-"]');
    const scope = mainProduct && mainProduct.id ? `#${CSS.escape(mainProduct.id)}` : '';
    const count = story.querySelectorAll('[data-pss-image]').length || 8;
    const firstImage = story.querySelector('[data-pss-image]');
    const aspectWidth = firstImage ? Number(firstImage.getAttribute('width')) || 1 : 1;
    const aspectHeight = firstImage ? Number(firstImage.getAttribute('height')) || 1 : 1;

    story.style.setProperty('--pss-step-count', String(count));
    story.style.setProperty('--pss-aspect-ratio', `${aspectWidth} / ${aspectHeight}`);

    const style = document.createElement('style');
    style.setAttribute('data-pss-rescue-style', '');
    style.textContent = `
      ${scope} .product-scroll-story { margin: 56px 0 0; }
      ${scope} .product-scroll-story__desktop {
        position: relative;
        height: max(320vh, calc(var(--pss-step-count, 8) * 72vh));
      }
      ${scope} .product-scroll-story__stage {
        position: sticky;
        top: 90px;
        min-height: calc(100vh - 110px);
        display: flex;
        align-items: center;
        justify-content: center;
      }
      ${scope} .product-scroll-story__visual {
        width: 100%;
        display: flex;
        justify-content: center;
      }
      ${scope} .product-scroll-story__media-stack {
        position: relative;
        width: min(92vw, 1200px);
        aspect-ratio: var(--pss-aspect-ratio, 1 / 1);
      }
      ${scope} .product-scroll-story__image {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        opacity: 0;
        transform: translate3d(0, 0, 0) scale(0.99);
        transition: opacity 240ms ease, transform 240ms ease;
        will-change: opacity, transform;
      }
      ${scope} .product-scroll-story__image.is-active {
        opacity: 1;
        transform: translate3d(0, 0, 0) scale(1);
      }
      @media (max-width: 989px) {
        ${scope} .product-scroll-story { margin: 36px 0 0; }
        ${scope} .product-scroll-story__desktop {
          height: max(240vh, calc(var(--pss-step-count, 8) * 58vh));
        }
        ${scope} .product-scroll-story__stage {
          top: 70px;
          min-height: calc(100vh - 90px);
        }
        ${scope} .product-scroll-story__media-stack { width: min(100vw, 1200px); }
        ${scope} .product-scroll-story__image {
          transform: translate3d(0, 0, 0) scale(1.2);
        }
        ${scope} .product-scroll-story__image.is-active {
          transform: translate3d(0, 0, 0) scale(1.28);
        }
      }
    `;
    mainSection.prepend(style);
  };

  const convertLegacyStory = () => {
    const mainSection = getMainSection();
    if (!mainSection || mainSection.querySelector('[data-pss-track]')) return;

    const legacyStory = mainSection.querySelector('[data-pdp-scroll-story]');
    if (!legacyStory) return;

    const legacyImages = Array.from(legacyStory.querySelectorAll('[data-pdp-scroll-story-item] img, .pdp-scroll-story__media img'));
    if (legacyImages.length < 2) return;

    const story = document.createElement('product-scroll-story');
    story.className = 'product-scroll-story';
    story.setAttribute('data-pss-count', String(legacyImages.length));

    const track = document.createElement('div');
    track.className = 'product-scroll-story__desktop';
    track.setAttribute('data-pss-track', '');

    const stage = document.createElement('div');
    stage.className = 'product-scroll-story__stage';

    const visual = document.createElement('div');
    visual.className = 'product-scroll-story__visual';
    visual.setAttribute('aria-live', 'polite');

    const stack = document.createElement('div');
    stack.className = 'product-scroll-story__media-stack';

    legacyImages.forEach((legacyImage, index) => {
      const image = document.createElement('img');
      image.className = `product-scroll-story__image${index === 0 ? ' is-active' : ''}`;
      image.setAttribute('data-pss-image', '');
      image.src = legacyImage.currentSrc || legacyImage.src;
      if (legacyImage.srcset) image.srcset = legacyImage.srcset;
      image.sizes = legacyImage.sizes || '(min-width: 990px) 92vw, 100vw';
      image.width = legacyImage.naturalWidth || Number(legacyImage.getAttribute('width')) || 1200;
      image.height = legacyImage.naturalHeight || Number(legacyImage.getAttribute('height')) || 960;
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.alt = legacyImage.alt || `step ${index + 1}`;
      stack.appendChild(image);
    });

    visual.appendChild(stack);
    stage.appendChild(visual);
    track.appendChild(stage);
    story.appendChild(track);
    injectStyles(mainSection, story);
    legacyStory.replaceWith(story);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', convertLegacyStory);
  } else {
    convertLegacyStory();
  }
})();
